import React from 'react';
import AnoniPreview from '../assets/images/projects/dev/previews/anoni.webp';
import BreakroomPreview from '../assets/images/projects/dev/previews/breakroom.webp';
import ClevercatPreview from '../assets/images/projects/dev/previews/clevercat.webp';
import IntellitermPreview from '../assets/images/projects/dev/previews/intelliterm.webp';
import LightbulbPreview from '../assets/images/projects/dev/previews/lightbulb.webp';
import UXIPreview from '../assets/images/projects/dev/previews/uxi.webp';
import VerbalizePhotosPreview from '../assets/images/projects/dev/previews/verbalizephotos.webp';
import IntrusiveThoughtsImage from '../assets/images/projects/art/intrusive-thoughts.jpg';
import KotikImage from '../assets/images/projects/art/kotik.jpg';
import MirrorOfMindImage from '../assets/images/projects/art/mirror-of-mind.jpg';
import ObeyImage from '../assets/images/projects/art/obey.jpg';
import RoseImage from '../assets/images/projects/art/rose.jpg';
import SweetCyanideImage from '../assets/images/projects/art/sweet-cyanide.jpg';
import UntitledImage from '../assets/images/projects/art/untitled.jpg';
import WaitingRoomImage from '../assets/images/projects/art/waiting-room.jpg';

import { Button, Stack } from '@mui/material';
import {
    Medium,
    type ArtProject,
    type DevProject,
    type Project,
    type ProjectType,
} from '../utils/Types';
import { githubRepo, My } from './My';

class Projects {
    readonly devProjects: DevProject[] = [
        {
            title: 'UXInsight',
            url: `${My.portfolio}/blob/main/projects/uxi.md`,
            preview: UXIPreview,
            description: (
                <span>
                    <strong>User experience analysis</strong> system that
                    extracts <strong>hidden insights</strong> and{' '}
                    <strong>intent</strong> from <strong>user behavior</strong>{' '}
                    to drive data-informed design <strong>decisions.</strong>
                </span>
            ),
            skills: [
                'TypeScript',
                'Next.js',
                'React',
                'D3.js',
                'MongoDB',
                'jest',
                'Material-UI',
                'Sass',
            ],
        },
        {
            title: 'Lightbulb',
            url: githubRepo('Lightbulb'),
            preview: LightbulbPreview,
            description: (
                <span>
                    Minimal <strong>dark theme</strong> that{' '}
                    <strong>intelligently illuminates</strong>{' '}
                    <strong>important elements</strong> in your code 💡
                    <br />
                    <br />
                    <Stack
                        style={{ fontSize: '14px' }}
                        sx={{ alignItems: { xs: 'center', md: 'flex-start' } }}
                    >
                        <Button
                            aria-label="download lightbulb button"
                            href="https://marketplace.visualstudio.com/items?itemName=ykray.lightbulb"
                            target="_blank"
                        >
                            Install for VS Code
                        </Button>
                    </Stack>
                </span>
            ),
            skills: ['Theme Design', 'Visual Studio Code development'],
        },
        {
            title: 'verbalize.photos',
            url: `${My.portfolio}/blob/main/projects/verbalize.md`,
            preview: VerbalizePhotosPreview,
            description: (
                <span>
                    <strong>AI-powered tool</strong> that helps{' '}
                    <strong>visually-impaired users</strong> experience{' '}
                    <strong>visual content</strong> through intelligent{' '}
                    <strong>image descriptions.</strong>
                </span>
            ),
            skills: [
                'AWS',
                'Azure',
                'TypeScript',
                'React',
                'Node.js',
                'Firebase',
                'Material-UI',
                'Sass',
            ],
        },
        {
            title: 'Breakroom',
            url: `${My.portfolio}/blob/main/projects/breakroom.md`,
            preview: BreakroomPreview,
            description: (
                <span>
                    Cross-platform <strong>hyperlocal group chat</strong> that
                    connects nearby users with{' '}
                    <strong>authentication,</strong> locally-trending{' '}
                    <strong>content discovery,</strong> and{' '}
                    <strong>gamified microeconomy.</strong>
                    <br />
                    <br />
                    (with{' '}
                    <a href="https://yegor.codes">Yegor Chernyshev</a>)
                </span>
            ),
            skills: [
                'TypeScript',
                'React',
                'React Native',
                'expo',
                'nx',
                'jest',
                'Firebase',
                'Material-UI',
                'Sass',
            ],
        },
        {
            title: 'Anoni',
            url: `${My.portfolio}/blob/main/projects/anoni.md`,
            preview: AnoniPreview,
            description: (
                <span>
                    <strong>Anonymous social platform</strong> that enables
                    private connections with <strong>authentication,</strong>{' '}
                    end-to-end <strong>encrypted chats,</strong> and{' '}
                    <strong>user discovery features.</strong>
                </span>
            ),
            skills: [
                'AWS',
                'TypeScript',
                'React',
                'React Native',
                'Node.js',
                'Firebase',
                'Cryptography',
                'Material-UI',
                'Sass',
            ],
        },
        {
            title: 'Intelliterm',
            url: `https://github.com/${My.github}/intelliterm`,
            preview: IntellitermPreview,
            description: (
                <span>
                    <strong>Open-source CLI tool</strong> that brings{' '}
                    <strong>AI chat</strong> directly to your{' '}
                    <strong>terminal</strong>, featuring{' '}
                    <strong>chat management,</strong>{' '}
                    <strong>code execution,</strong> and{' '}
                    <strong>GPT-3 & GPT-4 support.</strong>
                </span>
            ),
            skills: [
                'Python',
                'pytest',
                'openai',
                'CLI',
                'Machine Learning',
                'artificial intelligence',
            ],
        },
        {
            title: 'Clevercat',
            url: `${My.portfolio}/blob/main/projects/clevercat.md`,
            preview: ClevercatPreview,
            description: (
                <span>
                    <strong>Feature-rich Q&A system</strong> that includes{' '}
                    <strong>authentication,</strong>{' '}
                    <strong>full-text search,</strong> result{' '}
                    <strong>filtering,</strong> <strong>SQLi mitigation,</strong>{' '}
                    and <strong>karma system.</strong>
                </span>
            ),
            skills: [
                'TypeScript',
                'React',
                'PostgreSQL',
                'Node.js',
                'Material-UI',
                'Sass',
            ],
        }
    ];

    readonly artProjects: ArtProject[] = [
        {
            title: 'Waiting Room',
            medium: Medium.PenSharpie,
            tools: 'Pen, Sharpie',
            url: WaitingRoomImage,
            timestamp: new Date(2024, 2, 4),
        },
        {
            title: 'Profile',
            medium: Medium.Sharpie,
            tools: 'Sharpie',
            url: UntitledImage,
            timestamp: new Date(2023, 1, 10),
        },
        {
            title: 'Rose',
            medium: Medium.PencilSharpie,
            tools: 'Pencil, Sharpie',
            url: RoseImage,
            timestamp: new Date(2021, 10, 10),
        },
        {
            title: 'Kotik',
            medium: Medium.PencilSharpie,
            tools: 'Pencil, Sharpie',
            url: KotikImage,
            timestamp: new Date(2020, 2, 14),
        },
        {
            title: 'Sweet Cyanide',
            medium: Medium.Digital,
            tools: 'Photoshop, Blender',
            url: SweetCyanideImage,
            timestamp: new Date(2019, 10, 8),
        },
        {
            title: 'Field Theory',
            medium: Medium.Digital,
            tools: 'Photoshop',
            url: MirrorOfMindImage,
            timestamp: new Date(2019, 9, 15),
        },
        {
            title: 'Intrusive Thoughts',
            medium: Medium.Digital,
            tools: 'Photoshop',
            url: IntrusiveThoughtsImage,
            timestamp: new Date(2019, 7, 16),
        },
        {
            title: 'Repeat After Me',
            medium: Medium.Digital,
            tools: 'Photoshop',
            url: ObeyImage,
            timestamp: new Date(2021, 10, 17),
        },
    ];

    get = (type: ProjectType): Project[] => {
        switch (type) {
            case 'dev':
                return this.devProjects;
            case 'art':
                return this.artProjects;
        }
    };
}

export default new Projects();
